/**
 * sseClient — thin EventSource wrapper s exponential backoff reconnect.
 *
 * UC-001 admin sync UI (Phase 13) konzumuje SSE stream z
 * `/api/admin/sync/progress`. Per REQ-UI-024 implementuje:
 *   - Auto-reconnect po network/server disconnect s exp backoff (1s → 32s cap).
 *   - `connection` status callback umožní UI renderovat banner
 *     "Probíhá retry…" (mid-sync disconnect handling).
 *   - Listenery per event name ("progress") + onError / onConnection.
 *
 * Vrátí handle s `close()` cleanup; React hook obalí životnost komponenty.
 */

export type SseConnectionState = "connecting" | "open" | "retrying" | "closed";

export interface SseClientOptions {
  url: string;
  /** Event listener — event name → handler s parsovaným JSON datem. */
  onEvent: (eventName: string, data: unknown) => void;
  onError?: (err: Event) => void;
  onConnectionStateChange?: (state: SseConnectionState) => void;
  /** Initial reconnect delay v ms (default 1000). */
  initialDelayMs?: number;
  /** Max reconnect delay v ms (default 32000). */
  maxDelayMs?: number;
  /** Withcredentials flag pro SSE (default true — same-origin cookies). */
  withCredentials?: boolean;
  /** Extra named events to subscribe to (besides default "progress"). */
  extraEvents?: readonly string[];
}

export interface SseHandle {
  close(): void;
  getState(): SseConnectionState;
}

export function createSseClient(opts: SseClientOptions): SseHandle {
  const {
    url,
    onEvent,
    onError,
    onConnectionStateChange,
    initialDelayMs = 1000,
    maxDelayMs = 32_000,
    withCredentials = true,
    extraEvents = [],
  } = opts;

  let state: SseConnectionState = "connecting";
  let source: EventSource | null = null;
  let closed = false;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const listenedEvents = new Set<string>();

  function setState(next: SseConnectionState): void {
    if (state === next) return;
    state = next;
    onConnectionStateChange?.(next);
  }

  function nextDelay(): number {
    const exp = Math.min(maxDelayMs, initialDelayMs * 2 ** reconnectAttempt);
    return exp;
  }

  function attachListener(name: string): void {
    source?.addEventListener(name, (e) => {
      const messageEvent = e as MessageEvent<string>;
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(messageEvent.data);
      } catch {
        parsed = messageEvent.data;
      }
      onEvent(name, parsed);
    });
  }

  function ensureEventListener(name: string): void {
    if (listenedEvents.has(name)) return;
    listenedEvents.add(name);
    attachListener(name);
  }

  function connect(): void {
    if (closed) return;
    setState(reconnectAttempt === 0 ? "connecting" : "retrying");
    try {
      source = new EventSource(url, { withCredentials });
    } catch (err) {
      onError?.(err as Event);
      scheduleReconnect();
      return;
    }

    source.onopen = () => {
      reconnectAttempt = 0;
      setState("open");
    };

    source.onmessage = (e) => {
      // Default event (no `event:` line) → emit pod jménem "message".
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        parsed = e.data;
      }
      onEvent("message", parsed);
    };

    // Pre-attach "progress" listener — produkční event name z UC-001 backendu.
    // Caller může přidat další jména přes [extraEvents] (např. "alert" pro UC-007).
    ensureEventListener("progress");
    for (const name of extraEvents) {
      ensureEventListener(name);
    }

    source.onerror = (err) => {
      onError?.(err);
      // Browser EventSource auto-retries; my chceme controlled exp backoff
      // s notifikací UI → close + manual reconnect.
      source?.close();
      source = null;
      scheduleReconnect();
    };
  }

  function scheduleReconnect(): void {
    if (closed) return;
    setState("retrying");
    const delay = nextDelay();
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  connect();

  return {
    close() {
      closed = true;
      if (reconnectTimer != null) clearTimeout(reconnectTimer);
      source?.close();
      source = null;
      setState("closed");
    },
    getState() {
      return state;
    },
  };
}
